import { uniqBy, debounce } from 'lodash'
import { isFeatureEnabled, getVariant } from '@/vuex/modules/featureToggles.module'
import PresentationApi from '@/api/presentation'
import constant, { SYSTEM_FIELDS } from '@/constant'

export const EMOJI_AVATAR_ATTR = 'showEmojiAvatar'
export const PARTICIPANT_FIELDS_ATTR = 'participantFields'
export const PARTICIPANT_SELECTED_FIELDS = 'selectedParticipantFields'
export const READINESS_LAST_CHECKED_AT = 'readinessLastCheckedAt'
const MAX_ALLOWED_FIELD = 8

let debouncedUpdateParticipantField = null
let debouncedUpdateSelectedParticipantField = null

const actions = {
  updateShowEmojiAvatarSetting: async ({ dispatch }, { presentationId, value }) => {
    dispatch('presentationAttributeModule/updatePresentationAttributeAction', {
      presentationId,
      payload: {
        attributeName: EMOJI_AVATAR_ATTR,
        attributeValue: value,
      },
    }, { root: true })
  },
  updateParticipantFields: async ({ getters }, { presentationId, participantFields }) => {
    if (!getters.canUseCollectParticipantInfo || !participantFields || !presentationId) return Promise.resolve()
    const updateAdmission = PresentationApi.updatePresentationByPresentationId(presentationId, {
      audienceAdmission: participantFields,
    })
    const updateParticipantFields = PresentationApi.updateParticipantFields(presentationId, {
      participantFields,
    })

    return Promise.all([updateAdmission, updateParticipantFields])
  },
  updateParticipantInfoFields: async ({ getters, commit, dispatch }, { presentationId, updatedFields }) => {
    if (!getters.canUseCollectParticipantInfo) return Promise.resolve()
    const participantInfoSettings = {
      ...getters.participantInfoSettings,
      ...updatedFields,
    }

    commit('presentationAttributeModule/updatePresentationAttribute', {
      attributeName: PARTICIPANT_FIELDS_ATTR,
      attributeValue: participantInfoSettings,
    }, { root: true })

    if (!debouncedUpdateParticipantField) {
      debouncedUpdateParticipantField = debounce(
        // We need to bind everytime, otherwise, it will catch the wrong value
        // eslint-disable-next-line no-shadow
        (presentationId, participantInfoSettings) => dispatch('updateParticipantFields', { presentationId, participantFields: participantInfoSettings }),
        300,
      )
    }

    return debouncedUpdateParticipantField(presentationId, participantInfoSettings)
  },
  // eslint-disable-next-line no-unused-vars
  updateSelectedParticipantFields: async ({ getters, commit }, { presentationId, fields }) => {
    if (!getters.canUseCollectParticipantInfo) return Promise.resolve()

    commit('presentationAttributeModule/updatePresentationAttribute', {
      attributeName: PARTICIPANT_SELECTED_FIELDS,
      attributeValue: fields,
    }, { root: true })

    if (!debouncedUpdateSelectedParticipantField) {
      debouncedUpdateSelectedParticipantField = debounce(
        // We need to bind everytime, otherwise, it will catch the wrong value
        // eslint-disable-next-line no-shadow
        (presentationId, fields) => {
          PresentationApi.updateSelectedParticipantFields(presentationId, {
            selectedParticipantFields: fields,
          })
        },
        300,
      )
    }

    return debouncedUpdateSelectedParticipantField(presentationId, fields)
  },
  deletePresentationParticipantField: async ({ getters, dispatch }, { presentationId, fieldId }) => {
    const updatedFields = {
      fields: getters.participantFields.filter((f) => f.id !== fieldId),
    }

    dispatch('updateParticipantInfoFields', {
      presentationId,
      updatedFields,
    })

    const updatedSelectedFields = getters.selectedParticipantFieldsRaw.filter((f) => f.id !== fieldId)

    dispatch('updateSelectedParticipantFields', {
      presentationId,
      fields: updatedSelectedFields,
    })
  },
}

const _getters = {
  canUseToggleEmojiAvatar: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-toggle-emoji-avatar')
  },
  canUseCollectParticipantInfo: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-collect-participant-info')
  },
  maxAllowedField(_, __, ___, rootGetters) {
    const { payload } = getVariant(rootGetters, 'release-collect-participant-info')
    return Number(payload?.value) || MAX_ALLOWED_FIELD
  },
  participantInfoSettings: (_, __, rootState) => {
    return rootState?.presentationAttributeModule?.presentationAttribute?.[PARTICIPANT_FIELDS_ATTR] || {}
  },
  selectedParticipantFieldsRaw: (_, __, rootState) => {
    return rootState?.presentationAttributeModule?.presentationAttribute?.[PARTICIPANT_SELECTED_FIELDS] || []
  },
  readinessLastCheckedAt: (_, __, rootState) => {
    return rootState?.presentationAttributeModule?.presentationAttribute?.[READINESS_LAST_CHECKED_AT] || null
  },

  selectedParticipantFields: (_, getters) => {
    const participantFields = getters?.participantFields
    const selectedParticipantFields = getters?.selectedParticipantFieldsRaw

    return selectedParticipantFields?.map(({ id, required, hidden }) => {
      const fieldData = participantFields?.find(({ id: fieldId }) => fieldId === id)
      return {
        ...fieldData,
        id,
        required,
        hidden,
      }
    })
  },
  participantFields: (_, getters) => {
    const fields = getters?.participantInfoSettings?.fields || []
    const modifiedSystemField = fields.filter((field) => field.fieldType === SYSTEM_FIELDS)
    const systemFields = uniqBy([
      ...modifiedSystemField,
      ...constant.participantSystemFields,
    ], 'id')

    const availableFields = uniqBy([
      ...systemFields,
      ...fields,
    ], 'id')

    return availableFields.map((field) => {
      return {
        ...field,
        isSystemField: field.fieldType === SYSTEM_FIELDS,
      }
    })
  },
  participantFieldHeading: (_, getters) => {
    return getters.participantInfoSettings?.heading ?? 'Welcome to my presentation'
  },
  participantFieldsRequired: (_, getters) => {
    return getters.participantInfoSettings?.isAudienceAdmission || false
  },
  isShownEmojiAvatar: (_, getters, rootState) => {
    const attrValue = rootState.presentationAttributeModule?.presentationAttribute?.[EMOJI_AVATAR_ATTR]
    // If undefined it should set to true because it's the current default value
    const showEmojiValue = attrValue ?? true
    return getters.canUseToggleEmojiAvatar
      ? showEmojiValue
      : true
  },
  canUseHideCopyResponse: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-36736-hide-copy-response')
  },
}

export default {
  namespaced: true,
  actions,
  getters: _getters,
}
